import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* System-truth tone — authoritative, not promotional */
const MESSAGES = [
  'Everything is visible.',
  'Nothing escapes this layer.',
  'This is the system of record.',
  'Your system already knows.',
  'Nothing here needs guessing.',
  'This is your operational ground truth.',
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [msgIdx, setMsgIdx] = useState(0);
  const [fade, setFade] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  // SSO mode — read redirect_uri + module from query string
  const ssoParams = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const redirect_uri = sp.get('redirect_uri');
      const module = sp.get('module');
      const sso = sp.get('sso') === '1';
      if (redirect_uri && module) return { redirect_uri, module, sso };
    } catch { /* ignore */ }
    return null;
  })();

  // If user is already authenticated and hits SSO entry, auto-handoff immediately
  useEffect(() => {
    if (!ssoParams) return;
    const existing = sessionStorage.getItem('auth_token');
    if (!existing) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${BACKEND_URL}/api/auth/sso/issue-token`,
          { redirect_uri: ssoParams.redirect_uri, module: ssoParams.module },
          { headers: { Authorization: `Bearer ${existing}` } }
        );
        if (cancelled) return;
        const url = res.data?.redirect_url_combined
          || res.data?.redirect_url_fragment
          || res.data?.redirect_url_query;
        if (url) window.location.replace(url);
      } catch {
        // Token likely expired — fall back to manual login
        sessionStorage.removeItem('auth_token');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* Desktop: 2.5s dwell, Mobile: 3.5s — 300ms fade */
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const dwell = isMobile ? 3500 : 2500;
    const iv = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setMsgIdx((i) => (i + 1) % MESSAGES.length);
        setFade(true);
      }, 300);
    }, dwell);
    return () => clearInterval(iv);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        user_email: email, password, app_code: 'CONTROL_ROOM',
      });
      let data = response.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { setError('Login failed: Invalid response format'); return; }
      }
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        setError('Login failed: Unexpected response format'); return;
      }
      const token = data.access_token || data.token || data.jwt;
      if (!token || typeof token !== 'string') {
        setError('Login failed: No authentication token received'); return;
      }
      const { requires_password_reset, reset_token, reset_reason, allowed_apps } = data;
      if (requires_password_reset) {
        sessionStorage.setItem('forced_reset_token', reset_token);
        sessionStorage.setItem('forced_reset_email', email);
        navigate(`/reset-password?reason=${reset_reason || 'FIRST_LOGIN'}&forced=true`);
        return;
      }
      if (allowed_apps) sessionStorage.setItem('allowed_apps', JSON.stringify(allowed_apps));
      sessionStorage.setItem('user', JSON.stringify({
        user_id: data.user_id, user_name: data.user_name, user_email: data.user_email,
        is_super_admin: data.is_super_admin || false, platform_role_code: data.platform_role_code, org_id: data.org_id,
      }));
      login(token);

      // SSO handoff — mint enriched token & redirect back to engine callback
      if (ssoParams) {
        try {
          const issueRes = await axios.post(
            `${BACKEND_URL}/api/auth/sso/issue-token`,
            { redirect_uri: ssoParams.redirect_uri, module: ssoParams.module },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Send via combined URL — primary #token= (preferred), fallback ?token= (compatibility)
          const handoffUrl = issueRes.data?.redirect_url_combined
            || issueRes.data?.redirect_url_fragment
            || issueRes.data?.redirect_url_query;
          if (handoffUrl) {
            window.location.replace(handoffUrl);
            return;
          }
          setError('SSO handoff failed: no redirect URL returned');
          return;
        } catch (ssoErr) {
          const detail = ssoErr.response?.data?.detail || 'SSO handoff failed';
          setError(typeof detail === 'string' ? detail : 'SSO handoff rejected');
          return;
        }
      }

      navigate('/dashboard');
    } catch (err) {
      if (err.response?.status === 401) setError('Invalid email or password');
      else if (err.response?.status === 403) setError('Access denied. You do not have permission to access Control Room.');
      else setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root" data-testid="login-page">
      {/* Background layers */}
      <div className="login-bg-vignette" />
      <div className="login-bg-glow" />
      <div className="login-bg-grid" />
      <div className="login-bg-noise" />

      {/* CTRL watermark — architectural identity, desktop only */}
      <div className="ambient-engine-mark hidden lg:flex" aria-hidden="true">
        CTRL
      </div>

      {/* Layout: card left, messaging right */}
      <div className="login-layout">
        <div className="login-card-wrapper" data-testid="login-card">
          <div className="login-card">
            {/* Logo */}
            <div className="login-logo" data-testid="login-logo">
              <img src="/1os-logo.png" alt="1OS" />
            </div>

            {/* Mobile messaging */}
            <div className="login-mobile-msg" data-testid="dynamic-messaging-mobile">
              <p style={{ opacity: fade ? 1 : 0, transform: fade ? 'translateY(0)' : 'translateY(4px)' }}>
                {MESSAGES[msgIdx]}
              </p>
            </div>

            {/* Engine context */}
            <p className="login-context">Control Room</p>

            {/* Form */}
            <form onSubmit={handleSubmit} data-testid="login-form">
              {error && (
                <div className="login-error" data-testid="login-error">{error}</div>
              )}

              <div className="login-field">
                <label htmlFor="login-email">Email</label>
                <input
                  id="login-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com" required disabled={loading}
                  autoComplete="email" data-testid="login-email-input"
                />
              </div>

              <div className="login-field">
                <label htmlFor="login-password">Password</label>
                <input
                  id="login-password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password" required disabled={loading}
                  autoComplete="current-password" data-testid="login-password-input"
                />
              </div>

              <div className="login-forgot">
                <a href="/forgot-password">Forgot password?</a>
              </div>

              <button type="submit" disabled={loading} className="login-submit" data-testid="login-submit-button">
                {loading ? (
                  <span className="login-spinner-wrap">
                    <span className="login-spinner" />
                    Signing in
                  </span>
                ) : 'Sign In'}
              </button>
            </form>
          </div>
        </div>

        {/* Desktop messaging — right side */}
        <div className="login-desktop-msg" data-testid="dynamic-messaging-desktop">
          <p style={{ opacity: fade ? 1 : 0, transform: fade ? 'translateY(0)' : 'translateY(5px)' }}>
            {MESSAGES[msgIdx]}
          </p>
        </div>
      </div>

      {/* Footer — ultra-muted */}
      <div className="login-footer">1OS</div>
    </div>
  );
};

export default Login;
